import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

const DURATIONS = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
};

// A toast that offers an action (Undo, View…) stays longer, because the
// person has to read it and decide.
const ACTION_DURATION = 7000;

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  /**
   * @param {string} type     success | error | warning | info
   * @param {string} message
   * @param {number|Object} [durationOrOptions]  ms, or { duration, action: { label, onClick } }
   */
  const addToast = useCallback((type, message, durationOrOptions) => {
    const options = typeof durationOrOptions === 'object' && durationOrOptions !== null ? durationOrOptions : { duration: durationOrOptions };
    const id = ++toastId;
    const dur = options.duration || (options.action ? ACTION_DURATION : DURATIONS[type]) || 3000;
    setToasts((prev) => [...prev, { id, type, message, action: options.action || null }]);
    timersRef.current[id] = setTimeout(() => {
      removeToast(id);
    }, dur);
    return id;
  }, [removeToast]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  const toast = {
    success: useCallback((message, opts) => addToast('success', message, opts), [addToast]),
    error: useCallback((message, opts) => addToast('error', message, opts), [addToast]),
    warning: useCallback((message, opts) => addToast('warning', message, opts), [addToast]),
    info: useCallback((message, opts) => addToast('info', message, opts), [addToast]),
    /**
     * "Done — Undo". The common shape for reversible writes: say what
     * happened, offer to take it back, no confirm dialog beforehand.
     */
    undo: useCallback((message, onUndo, opts = {}) => addToast(opts.type || 'success', message, { ...opts, action: { label: 'Undo', onClick: onUndo } }), [addToast]),
  };

  return (
    <ToastContext.Provider value={{ toasts, toast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context.toast;
}

export function useToastContext() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
}
